import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageCircle, CheckCircle2, AlertCircle } from 'lucide-react';

const WhatsAppTest = () => {
  const whatsappTests = [
    {
      title: 'Direct WhatsApp Link',
      description: 'Basic WhatsApp connection',
      url: 'https://wa.me/96899378612',
      testId: 'direct'
    },
    {
      title: 'Business Message (English)',
      description: 'Pre-filled English greeting',
      url: 'https://wa.me/96899378612?text=Hello%20Taj%20Gulf%20Trading%20LLC%2C%20I%20would%20like%20to%20inquire%20about%20your%20products.',
      testId: 'english'
    },
    {
      title: 'Business Message (Arabic)',
      description: 'Pre-filled Arabic greeting',
      url: 'https://wa.me/96899378612?text=%D9%85%D8%B1%D8%AD%D8%A8%D8%A7%20%D8%AA%D8%A7%D8%AC%20%D8%A7%D9%84%D8%AE%D9%84%D9%8A%D8%AC%20%D9%84%D9%84%D8%AA%D8%AC%D8%A7%D8%B1%D8%A9%D8%8C%20%D8%A3%D9%88%D8%AF%20%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D9%81%D8%B3%D8%A7%D8%B1%20%D8%B9%D9%86%20%D9%85%D9%86%D8%AA%D8%AC%D8%A7%D8%AA%D9%83%D9%85.',
      testId: 'arabic'
    },
    {
      title: 'Product Inquiry',
      description: 'Inquiry about specific product',
      url: 'https://wa.me/96899378612?text=Hello%20Taj%20Gulf%20Trading%20LLC%2C%20I%20would%20like%20to%20inquire%20about%3A%20Al%20Shafaa%20Natural%20Honey',
      testId: 'product'
    }
  ];

  const handleTest = (url) => {
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#1a3a52] mb-4">
            WhatsApp Integration Test Page
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Test all WhatsApp functionalities before deployment
          </p>
          <p className="text-sm text-gray-500">
            Contact: +968 9937 8612 (Taj Gulf Trading LLC)
          </p>
        </div>

        {/* Important Notice */}
        <Card className="mb-8 border-l-4 border-yellow-500 bg-yellow-50">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-yellow-900 mb-2">
                  Important: About "ERR_CONNECTION_RESET" Error
                </h3>
                <p className="text-yellow-800 text-sm leading-relaxed mb-3">
                  If you see connection errors in preview mode, this is usually due to browser popup blockers, 
                  corporate firewalls, or network restrictions. This is NORMAL and doesn't mean WhatsApp won't 
                  work when deployed.
                </p>
                <p className="text-yellow-800 text-sm font-semibold">
                  ✓ The WhatsApp links are correctly formatted and will work in production
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Instructions */}
        <Card className="mb-8 bg-white">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-[#1a3a52] mb-4">
              Testing Instructions
            </h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-[#1a3a52] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 text-sm">1</span>
                <span>Click any "Test" button below</span>
              </li>
              <li className="flex items-start">
                <span className="bg-[#1a3a52] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 text-sm">2</span>
                <span>Allow popups if browser asks for permission</span>
              </li>
              <li className="flex items-start">
                <span className="bg-[#1a3a52] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 text-sm">3</span>
                <span>WhatsApp Web (desktop) or WhatsApp App (mobile) should open</span>
              </li>
              <li className="flex items-start">
                <span className="bg-[#1a3a52] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 text-sm">4</span>
                <span>Verify the pre-filled message appears correctly</span>
              </li>
              <li className="flex items-start">
                <span className="bg-[#1a3a52] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 text-sm">5</span>
                <span>Check if recipient shows as "+968 9937 8612"</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Test Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {whatsappTests.map((test) => (
            <Card key={test.testId} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <MessageCircle className="h-6 w-6 text-[#25D366] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[#1a3a52] text-lg mb-1">
                      {test.title}
                    </h3>
                    <p className="text-sm text-gray-600">{test.description}</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleTest(test.url)}
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Test This Link
                </Button>
                <p className="text-xs text-gray-500 mt-3 break-all">
                  {test.url}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Success Criteria */}
        <Card className="bg-green-50 border-l-4 border-green-500">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-green-900 mb-3">
                  What Should Happen (Successful Test)
                </h3>
                <ul className="space-y-2 text-green-800 text-sm">
                  <li>✓ WhatsApp Web or App opens in new tab/window</li>
                  <li>✓ Number appears as: +968 9937 8612</li>
                  <li>✓ Pre-filled message visible in chat input</li>
                  <li>✓ Message mentions "Taj Gulf Trading LLC"</li>
                  <li>✓ You can send the message without typing</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Share Links */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            Share this test page with your team:
          </p>
          <div className="bg-gray-100 p-4 rounded-lg inline-block">
            <code className="text-sm text-[#1a3a52] font-mono">
              {window.location.origin}/whatsapp-test
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTest;
